import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ACCESS_TOKEN } from "../../lib/cookie";
import { getBestPost } from "../../api/postAPI";

import styled from "styled-components";
import { Carousel, Button, Row, Col, Image } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { CarouselRef } from "antd/es/carousel";
import { MainColor, SubColor } from "../../style/styleValue";
import { useLang } from "../../i18n";

const FeaturedSection = () => {
  const [bestPost, setBestPost] = useState<any[]>([]);

  const carouselRef = useRef<CarouselRef>(null);

  const navigate = useNavigate();
  const lang = useLang();

  const handlePostClick = (postId: number) => {
    const hasToken = !!localStorage.getItem(ACCESS_TOKEN);

    if (hasToken) {
      navigate(`/post/${postId}`);
    } else {
      alert(lang === 'en' ? "Please log in to view this case." : "해당 게시물은 로그인 후 열람 가능합니다.");
    }
  };

  useEffect(() => {
    getBestPost().then(data => {
      setBestPost(data);
    })
  }, []);

  return (
    <Container>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <SectionHeader>
            <h2 className="section-label">Featured Case</h2>
          </SectionHeader>
          
          <StyledCarousel 
            ref={carouselRef}
            autoplay 
            effect="fade" 
            dots={{ className: "custom-dots" }}
          >
            {bestPost.map((item) => (
              <div key={item.id}>
                <WideCard>
                  <ImageWrapper>
                    <img src={
                      item.postThumbnail ?
                      `${process.env.REACT_APP_BACKEND_URL}/api/photo/${item.postThumbnail}` :
                      "/img/sample_thumbnail/sample.png"}
                      alt={item.title} />
                    <GradientOverlay />
                  </ImageWrapper>
                  <ContentLayer>
                    <CardTitle>{item.title}</CardTitle>
                    <ViewButton type="primary" onClick={() => handlePostClick(item.id)}>
                      View Case <ArrowRightOutlined />
                    </ViewButton>
                  </ContentLayer>
                </WideCard>
              </div>
            ))}
          </StyledCarousel>
        </Col>

        <Col xs={24} lg={8}>
          <PosterCard>
            <div className="poster-content">
              <Image
                src="/img/main/260713poster.png"
                alt="Featured Photo"
                preview={{ mask: lang === 'en' ? "Click to enlarge" : "클릭하여 확대" }}
              />
            </div>
          </PosterCard>
        </Col>
      </Row>
    </Container>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 15px;
  height: 32px;

  .section-label {
    font-size: 1.25rem;
    color: #333;
    margin: 0;
  }
`;

const StyledCarousel = styled(Carousel)`
  margin-bottom: 40px; 

  .slick-list {
    border-radius: 4px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  }

  .slick-dots {
    position: relative;
    bottom: -25px !important;
    margin: 0;
    display: flex !important;
    justify-content: center;
    list-style: none;

    li {
      width: 40px;
      height: 4px;
      margin: 0 3px;
      padding: 0;
      
      button {
        width: 100%;
        height: 100%;
        background: #e5e5e5;
        opacity: 1;
        border-radius: 2px;
        transition: all 0.3s ease;

        &::before,
        &::after {
          display: none !important;
        }
      }
      
      &.slick-active {
        button {
          background-color: ${SubColor} !important;
          width: 100%;
        }
        &::before,
        &::after {
          display: none !important;
        }
      }
    }
  }
`;

const WideCard = styled.div`
  position: relative;
  display: flex !important;
  align-items: center;
  height: 550px;
  background: #fff;
  overflow: hidden;
  border: 1px solid #f0f2f5;
  border-radius: 12px;
`;

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 70%;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(2.5) contrast(1.05) brightness(1); 
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right, 
    rgba(255, 255, 255, 0) 0%, 
    rgba(255, 255, 255, 0) 55%,
    rgba(255, 255, 255, 1) 95% 
  );
`;

const ContentLayer = styled.div`
  position: relative;
  z-index: 2;
  margin-left: auto;
  width: 50%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 400;
  color: #000;
  margin-bottom: 35px;
  line-height: 1.2;
`;

const ViewButton = styled(Button)`
  width: fit-content;
  height: 44px;
  padding: 0 25px;
  border-radius: 6px;
  background-color: ${SubColor} !important;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${MainColor} !important;
  }
`;

const PosterCard = styled.div`
  height: 550px;
  border-radius: 4px;
  margin-top: 47px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  background: #fff;

  .poster-content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-image {
    max-width: 100%;
    max-height: 100%;
    cursor: pointer;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

export default FeaturedSection;